import { useMemo } from "react";
import {
  calculateAllCashOutV2Fields,
  type CashOutV2Input,
  type CashOutV2Calculated,
} from "@restaurant-staff-portal/shared/utils";
import type {
  ReconciliationStatus,
  OwedDirection,
} from "@restaurant-staff-portal/shared/types";

/**
 * Form values as they come from React Hook Form
 * All fields may be undefined during form editing
 */
export interface CashOutV2FormValues {
  netSalesTotal?: number;
  totalGratuityAndFees?: number;
  taxes?: number;
  giftCardSales?: number;
  discounts?: number;
  totalNonCashTips?: number;
  staffAccountAmount?: number;
  staffAccountTip?: number;
  staffAccountGratuity?: number;
  houseAccountAmount?: number;
  houseAccountTip?: number;
  houseAccountGratuity?: number;
  cashAmount?: number;
  cashTip?: number;
  cashGratuity?: number;
  creditAmount?: number;
  creditTip?: number;
  creditGratuity?: number;
  giftCardAmount?: number;
  giftCardTip?: number;
  giftCardGratuity?: number;
}

/**
 * Extended calculations result with UI helper data
 */
export interface CashOutV2CalculationsResult extends CashOutV2Calculated {
  reconciliationStatus: ReconciliationStatus;
  owedDirection: OwedDirection;
  owedAmount: number; // Absolute value for display
}

/**
 * Convert form values (with possible undefined) to calculation input (with 0 defaults)
 */
function formValuesToInput(values: CashOutV2FormValues): CashOutV2Input {
  return {
    netSalesTotal: values.netSalesTotal ?? 0,
    totalGratuityAndFees: values.totalGratuityAndFees ?? 0,
    taxes: values.taxes ?? 0,
    giftCardSales: values.giftCardSales ?? 0,
    discounts: values.discounts ?? 0,
    totalNonCashTips: values.totalNonCashTips ?? 0,
    staffAccountAmount: values.staffAccountAmount ?? 0,
    staffAccountTip: values.staffAccountTip ?? 0,
    staffAccountGratuity: values.staffAccountGratuity ?? 0,
    houseAccountAmount: values.houseAccountAmount ?? 0,
    houseAccountTip: values.houseAccountTip ?? 0,
    houseAccountGratuity: values.houseAccountGratuity ?? 0,
    cashAmount: values.cashAmount ?? 0,
    cashTip: values.cashTip ?? 0,
    cashGratuity: values.cashGratuity ?? 0,
    creditAmount: values.creditAmount ?? 0,
    creditTip: values.creditTip ?? 0,
    creditGratuity: values.creditGratuity ?? 0,
    giftCardAmount: values.giftCardAmount ?? 0,
    giftCardTip: values.giftCardTip ?? 0,
    giftCardGratuity: values.giftCardGratuity ?? 0,
  };
}

/**
 * Check if reconciliation value is balanced (within penny tolerance)
 */
function isBalanced(value: number): boolean {
  return Math.abs(value) <= 0.01;
}

/**
 * Get owed direction from amount
 */
function getOwedDirection(amount: number): OwedDirection {
  if (amount < -0.01) return "to_server";
  if (amount > 0.01) return "to_house";
  return "balanced";
}

/**
 * Hook for real-time CashOut V2 calculations
 *
 * Watches form values and returns all calculated fields plus UI helpers.
 *
 * @param formValues - Current form values from React Hook Form's useWatch
 * @param houseTipPercentage - House tip percentage as decimal (default: 0.09)
 * @returns All calculated values plus reconciliation status and owed direction
 *
 * @example
 * ```tsx
 * const { control } = useForm<CashOutV2FormValues>();
 * const watchedValues = useWatch({ control });
 * const calculations = useCashOutV2Calculations(watchedValues);
 *
 * // Use in JSX
 * <TextField value={formatCurrency(calculations.grossSales)} disabled />
 * ```
 */
export function useCashOutV2Calculations(
  formValues: CashOutV2FormValues,
  houseTipPercentage: number = 0.09
): CashOutV2CalculationsResult {
  return useMemo(() => {
    const input = formValuesToInput(formValues);
    const calculated = calculateAllCashOutV2Fields(input, houseTipPercentage);

    const reconciliationStatus: ReconciliationStatus = {
      salesBalanced: isBalanced(calculated.salesReconciliation),
      tipsBalanced: isBalanced(calculated.tipsReconciliation),
      salesDifference: calculated.salesReconciliation,
      tipsDifference: calculated.tipsReconciliation,
    };

    const owedDirection = getOwedDirection(calculated.owedToServerHouse);
    const owedAmount = Math.abs(calculated.owedToServerHouse);

    return {
      ...calculated,
      reconciliationStatus,
      owedDirection,
      owedAmount,
    };
  }, [formValues, houseTipPercentage]);
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}
